import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaClock, FaUsers, FaStar, FaChevronDown, FaChevronUp, FaGem, FaAward } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './CoursesPage.css';

const courseCatalog = [
  {
    id: 'jewellery-fundamentals',
    title: 'Jewellery Fundamentals',
    subtitle: 'For Beginners in the Jewellery Industry',
    description: 'A beginner-friendly foundation in the jewellery industry covering history, jewellery types, key terms, Indian and global markets, materials, gemstones, golden calculations, design basics, trends, consumer behaviour, and career pathways.',
    duration: '1 month',
    instructor: 'Nancy Johar',
    level: 'Beginner',
    image: '/j1.png',
    topics: [
      'Introduction to the jewellery industry basics',
      'History and evolution of jewellery',
      'Jewellery types, categories, and key terminology',
      'Global and Indian jewellery markets',
      'Customer preferences and market trends',
      'Golden calculations',
      'Metals, gemstones, and alternate jewellery materials',
      'Jewellery-making techniques',
      'Hallmarking, certifications, and purity standards',
      'Design elements, sketching, proportions, and symmetry',
      'Colour theory in jewellery',
      'Occasion, gender, and cultural influences',
      'Packaging and career pathways in jewellery'
    ],
    features: [
      'Online recordings for online students',
      'Course completion certificate',
      'Access to all future workshops',
      'Live online classes'
    ]
  },
  {
    id: 'manual-jewellery-designing',
    title: 'Manual Jewellery Designing',
    subtitle: 'Traditional Hand Sketching and Rendering',
    description: 'A practical programme that builds jewellery design skills through pencil sketching, colour, rendering, composition, stone placement, and design presentation. It helps learners turn ideas into professional jewellery sketches and a stronger design portfolio.',
    duration: 'Contact for schedule',
    instructor: 'Nancy Johar',
    level: 'Foundation',
    image: '/j2.png',
    topics: [
      'History and evolution of jewellery design',
      'Grading and selection of gemstones',
      'Elements and fundamentals of line sketching',
      'Concept development and geometrical shape sketching',
      'Rendering, colouring, and shading techniques',
      'Gemstone and diamond settings',
      'Symmetry and asymmetry in jewellery design',
      'Inspiration-led designing',
      'Rings and their insights',
      'Ear ornaments and their insights',
      'Bangles and their insights',
      'Pendants and their insights',
      'Final project'
    ],
    features: [
      'Complimentary designing toolkit',
      'Practice designs and modules',
      'Online recordings for online students',
      'Course completion certificate',
      'Internship and additional internship certificate',
      'Access to all future workshops',
      'Live online and offline classes'
    ]
  },
  {
    id: 'procreate-jewellery-designing',
    title: 'Procreate Jewellery Designing',
    subtitle: 'Basic to Advanced Digital Jewellery Illustration',
    description: 'A digital jewellery design programme for creating professional illustrations in Procreate on iPad. Learners move from basic sketching to digital rendering, custom brushes, gemstones, portfolio building, and final project presentation.',
    duration: 'Contact for schedule',
    instructor: 'Nancy Johar',
    level: 'Digital',
    image: '/j3.png',
    topics: [
      'Introduction to Procreate and basic sketching',
      'Elements and fundamentals of jewellery sketching',
      'Concept development and mood boards',
      'Symmetry and its use in digital design',
      'Colour palettes, colour theory, and brush making',
      'Rendering metals, gemstones, and cabochons',
      'Diamond settings',
      'Ring designing',
      'Earring designing',
      'Bangle and bracelet designing',
      'Pendant designing',
      'Necklace designing',
      'Customisation and jewellery presentation backgrounds',
      'Digital portfolio',
      'Final project'
    ],
    essentials: [
      'Apple iPad',
      'Apple Pencil',
      'Procreate app for iOS'
    ],
    features: [
      '100+ jewellery brushes',
      '10+ colour palettes',
      '100+ PNG gemstone library',
      'Practice designs and modules',
      'Live recordings for online students',
      'Course completion certificate',
      'Internship and additional internship certificate',
      'Access to all future workshops',
      'Live online, live offline, and pre-recorded classes'
    ]
  },
  {
    id: 'self-transformation-with-jewellery',
    title: 'Self-Transformation with Jewellery',
    subtitle: 'Styling, Confidence, and Personal Expression',
    description: 'A unique programme exploring the connection between jewellery, personal style, self-expression, and confidence. It covers jewellery styling by face shape, neckline, outfits, colour coordination, occasions, personal branding, and basic personalised jewellery creation.',
    duration: 'Contact for schedule',
    instructor: 'Nancy Johar',
    level: 'Styling',
    image: '/j4.png',
    topics: [
      'Necklace lengths and neck-size styling',
      'Jewellery for every face shape',
      'Seasonal themes in jewellery styling',
      'Choosing the right necklace for every neckline',
      'Jewellery colours for every outfit',
      'Jewellery as confidence and individuality',
      'Non-verbal communication through jewellery',
      'Basic techniques for personalised jewellery',
      'Therapeutic process of personalised jewellery making',
      'Celebrity and media influence on jewellery culture',
      'Jewellery choices in professional environments'
    ],
    features: [
      'Live recordings for online students',
      'Course completion certificate',
      'Live online and offline classes'
    ]
  },
  {
    id: 'platinum-course',
    title: 'Platinum Course',
    subtitle: 'Complete Jewellery Learning Programme with Internship',
    description: 'A comprehensive all-in-one programme that combines Jewellery Fundamentals, Manual Jewellery Designing, Procreate Jewellery Designing, Self-Transformation with Jewellery, and a professional internship for a complete jewellery design learning journey.',
    duration: 'Contact for schedule',
    instructor: 'Nancy Johar',
    level: 'Comprehensive',
    image: '/images/gl-jewel-academy-logo.png',
    topics: [
      'Jewellery industry fundamentals',
      'Materials, gemstones, market trends, and consumer behaviour',
      'Manual sketching, rendering, and jewellery illustration',
      'Design concepts, proportions, detailing, and presentation',
      'Digital jewellery design in Procreate',
      'Custom brushes, colour palettes, and gemstone libraries',
      'Digital portfolio development',
      'Jewellery styling and self-expression',
      'Personal branding and confidence through jewellery',
      'Hands-on practice designs',
      'Final manual and digital projects',
      'Professional internship experience'
    ],
    essentials: [
      'Apple iPad',
      'Apple Pencil',
      'Procreate app for iOS'
    ],
    features: [
      'Complimentary manual designing toolkit',
      '100+ jewellery brushes',
      '10+ colour palettes',
      '100+ PNG gemstone library',
      'Practice designs and modules',
      'Live recordings for online students',
      'Course completion certificate',
      'Internship and additional internship certificate',
      'Access to all future workshops',
      'Live online and offline classes'
    ]
  }
];

const getDifficultyColor = (level) => {
  switch (level.toLowerCase()) {
    case 'beginner':
      return '#4CAF50';
    case 'foundation':
      return '#FF9800';
    case 'digital':
      return '#2196F3';
    case 'styling':
      return '#AF8E4B';
    case 'comprehensive':
      return '#7B4F2A';
    default:
      return '#2196F3';
  }
};

const CoursesPage = () => {
  const [expandedCourse, setExpandedCourse] = useState(null);
  const navigate = useNavigate();

  const toggleCourseExpansion = (courseId) => {
    setExpandedCourse(expandedCourse === courseId ? null : courseId);
  };

  return (
    <div className="courses-page">
      <div className="container">
        <motion.div
          className="courses-header"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1>Our Jewellery Design Courses</h1>
          <p>Build practical jewellery skills through fundamentals, manual design, digital illustration, styling, and internship-led learning.</p>
        </motion.div>

        <div className="courses-grid">
          {courseCatalog.map((course, index) => (
            <motion.div
              key={course.id}
              className="course-card"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <div className="course-image">
                <img
                  src={course.image}
                  alt={course.title}
                />
                <div className="course-level" style={{ backgroundColor: getDifficultyColor(course.level) }}>
                  {course.level}
                </div>
              </div>

              <div className="course-content">
                <div className="course-header">
                  <h3>{course.title}</h3>
                  <p className="course-subtitle">{course.subtitle}</p>
                  <div className="course-meta">
                    <div className="meta-item">
                      <FaClock />
                      <span>{course.duration}</span>
                    </div>
                    <div className="meta-item">
                      <FaUsers />
                      <span>{course.instructor}</span>
                    </div>
                  </div>
                </div>

                <p className="course-description">{course.description}</p>

                <div className="course-topics-preview">
                  {course.topics.slice(0, 3).map((topic, topicIndex) => (
                    <div key={topicIndex} className="topic-item">
                      <FaGem className="topic-icon" />
                      <span>{topic}</span>
                    </div>
                  ))}
                  {course.topics.length > 3 && (
                    <div className="topic-more">
                      +{course.topics.length - 3} more topics
                    </div>
                  )}
                </div>

                <button
                  className="expand-btn"
                  onClick={() => toggleCourseExpansion(course.id)}
                >
                  {expandedCourse === course.id ? (
                    <>
                      <span>Show Less</span>
                      <FaChevronUp />
                    </>
                  ) : (
                    <>
                      <span>View Full Curriculum</span>
                      <FaChevronDown />
                    </>
                  )}
                </button>

                <AnimatePresence>
                  {expandedCourse === course.id && (
                    <motion.div
                      className="expanded-content"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="full-topics">
                        <h4>Course Curriculum</h4>
                        <div className="topics-list">
                          {course.topics.map((topic, topicIndex) => (
                            <div key={topicIndex} className="topic-detail">
                              <div className="topic-info">
                                <FaGem className="topic-icon" />
                                <span className="topic-name">{topic}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {course.essentials && (
                        <div className="course-features">
                          <h4>Essentials Required</h4>
                          <ul>
                            {course.essentials.map((essential) => (
                              <li key={essential}>{essential}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="course-features">
                        <h4>What We Offer</h4>
                        <ul>
                          {course.features.map((feature) => (
                            <li key={feature}>{feature}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="enquiry-section">
                        <div className="enrollment-info">
                          <div className="enquiry-note">
                            <span className="enquiry-label">Interested in this course?</span>
                            <span className="enquiry-copy">Reach out to GL Jewel Academy for batches, mode, and admission details.</span>
                          </div>
                          <div className="enrollment-benefits">
                            <div className="benefit">
                              <FaAward />
                              <span>Certificate Guidance</span>
                            </div>
                            <div className="benefit">
                              <FaUsers />
                              <span>Online and Offline Options</span>
                            </div>
                            <div className="benefit">
                              <FaStar />
                              <span>Expert-led Learning</span>
                            </div>
                          </div>
                        </div>

                        <div className="enrollment-actions">
                          <button
                            className="btn btn-primary enroll-btn"
                            onClick={() => navigate('/contact')}
                          >
                            Enquire Now
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="cta-section"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="cta-content">
            <h2>Ready to Start Your Jewellery Design Journey?</h2>
            <p>Speak with us about course modes, batches, and the right learning path for your goals.</p>
            <div className="cta-buttons">
              <button
                className="btn btn-primary"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                Browse All Courses
              </button>
              <button
                className="btn btn-outline"
                onClick={() => navigate('/contact')}
              >
                Contact Us
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CoursesPage;
